import { useEffect, useRef, useState } from 'react';

import Button from 'lib/common/components/Button/Button';
import Text from 'lib/common/components/Text';
import DescriptivePill from 'lib/common/components/atoms/DescriptivePills/components/DescriptivePill';

import { useAgentPreferencesContext } from '../../../Context';
import { DEFAULT_LOCATION } from '../../constants';
import { MyLocationFormStep } from '../../enums/MyLocationFormStep';
import { TCandidateAddress } from '../../types';
import { TConfirmCallbackNumberAndCandidateAddressesProps } from '../../types/ConfirmCallbackNumberAndCandidateAddressesProps';

import styles from './confirm-callback-modal-and-candidate-addresses.module.scss';

export default function ConfirmCallbackNumberAndCandidateAddresses({
  props: {
    control,
    setValue,
    errors,
    clearErrors,
    validateCallbackNumber,
    handleFormSubmit,
    numbersMatch,
    setStep,
    HookFormInput,
    candidateAddresses,
    shouldConfirmCallbackNumber,
    getValues,
  },
}: {
  props: TConfirmCallbackNumberAndCandidateAddressesProps;
}) {
  const {
    state: { agentPreference },
  } = useAgentPreferencesContext();

  // Storing in a ref, to prevent re-render and hiding the
  // phone input field when handleSubmit is working on async action
  // which causes the value to become false
  const shouldConfirmCallbackNumberRef = useRef(shouldConfirmCallbackNumber);

  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);

  useEffect(() => {
    if (!candidateAddresses.length) {
      return;
    }

    const { location } = getValues();

    setValue('updatedLocation', {
      ...DEFAULT_LOCATION,
      // Pass unitNumber through, since it doesn't get validated by AWS
      // or returned in the candidate addresses
      unitNumber: location.unitNumber,
      ...candidateAddresses[selectedAddressIndex],
    });
  }, [candidateAddresses, selectedAddressIndex]);

  return (
    <div className={styles['confirm-callback-modal-and-candidate-addresses-modal-step__container']}>
      <div className={styles['confirm-callback-modal-and-candidate-addresses-modal-step__content']}>
        <Text>Confirm your emergency call details are correct.</Text>
        {shouldConfirmCallbackNumberRef.current ? (
          <div>
            <div>
              <Text type="heading3" className="mb-5">
                Callback Number
              </Text>
              <Text type="label">This needs to match the number you entered in the last step.</Text>
            </div>
            <div className="mt-10">
              <HookFormInput
                InputLabelProps={{ shrink: true, required: true }}
                name="confirmCallbackNumber"
                data-testid="confirm-callback-number-input"
                validationError={errors['confirmCallbackNumber']}
                onBlur={() => validateCallbackNumber()}
                clearErrors={clearErrors}
                fullWidth
                control={control}
                ariaRequired
                placeholder="(123) 456-7890"
                type="PHONE"
              />
            </div>
          </div>
        ) : null}
        {candidateAddresses.length ? (
          <div>
            <div>
              <Text type="heading3" className="mb-5">
                Address
              </Text>
              <Text type="label">We couldn’t confirm your exact address. Here are some close matches.</Text>
            </div>
            <div className="mt-10 flex-column next-to-10">
              {candidateAddresses.map((address: TCandidateAddress, index) => (
                <DescriptivePill
                  selected={selectedAddressIndex === index}
                  heading={`${address.streetNumber ? address.streetNumber : ''}${address.streetInfo ? ` ${address.streetInfo}` : ''}`}
                  description={`${address.city}, ${address.state} ${address.postalCode}`}
                  onClick={() => {
                    setSelectedAddressIndex(index);
                  }}
                  key={index}
                  maxWidth="100%"
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
      <div className="flex next-to-10 justify-end">
        <Button
          onClick={() => {
            setValue('confirmCallbackNumber', '');
            setStep(MyLocationFormStep.FORM);
          }}
          styleType="SECONDARY"
        >
          Back
        </Button>
        <Button
          disabled={shouldConfirmCallbackNumberRef.current && !numbersMatch}
          styleType="PRIMARY"
          data-testid="confirm-callback-and-addresses-continue-button"
          asyncAction
          onClick={() => handleFormSubmit()}
        >
          {!agentPreference.location ? 'Continue' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
